import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  AssetView,
  AssetCondition,
  EmployeeSimpleView,
  IssueAssetInput,
  searchEmployees,
  generateOtp,
} from "../../../../api/General/assets";

interface IssueAssetModalProps {
  open: boolean;
  onClose: () => void;
  asset: AssetView | null;
  onIssue: (input: IssueAssetInput) => Promise<void>;
  issuing: boolean;
  requireOtp: boolean;
}

const IssueAssetModal: React.FC<IssueAssetModalProps> = ({
  open,
  onClose,
  asset,
  onIssue,
  issuing,
  requireOtp,
}) => {
  const { t } = useTranslation();

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState<EmployeeSimpleView[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSimpleView | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [purpose, setPurpose] = useState("");
  const [condition, setCondition] = useState<AssetCondition>(AssetCondition.Good);
  const [notes, setNotes] = useState("");
  const [otp, setOtp] = useState("");
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [generatingOtp, setGeneratingOtp] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset form on open
  useEffect(() => {
    if (open && asset) {
      setEmployeeSearch("");
      setSelectedEmployee(null);
      setShowDropdown(false);
      setIssueDate(new Date().toISOString().split("T")[0]);
      setExpectedReturnDate("");
      setPurpose("");
      setCondition(asset.condition);
      setNotes("");
      setOtp("");
      setOtpGenerated(false);
      setErrors({});
    }
  }, [open, asset]);

  // Debounced employee search
  useEffect(() => {
    if (employeeSearch.length < 2 || selectedEmployee) {
      setEmployees([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await searchEmployees(employeeSearch);
        if (result.errors.length === 0) {
          setEmployees(result.payload);
          setShowDropdown(result.payload.length > 0);
        }
      } catch {
        // silent
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [employeeSearch, selectedEmployee]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectEmployee = useCallback((emp: EmployeeSimpleView) => {
    setSelectedEmployee(emp);
    setEmployeeSearch(emp.fullName);
    setShowDropdown(false);
    // OTP is bound to a specific employee — clear any stale code when the target changes
    setOtpGenerated(false);
    setOtp("");
    setErrors((prev) => ({ ...prev, employee: "" }));
  }, []);

  const handleGenerateOtp = useCallback(async () => {
    if (!asset || !selectedEmployee) return;
    setGeneratingOtp(true);
    try {
      const result = await generateOtp(asset.id, selectedEmployee.id);
      if (result.errors.length === 0) {
        setOtpGenerated(true);
      }
    } catch {
      // silent
    } finally {
      setGeneratingOtp(false);
    }
  }, [asset, selectedEmployee]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!selectedEmployee) newErrors.employee = t("assets.validation.selectEmployee");
    if (!issueDate) newErrors.issueDate = t("assets.validation.issueDateRequired");
    if (!purpose.trim()) newErrors.purpose = t("assets.validation.purposeRequired");
    if (requireOtp) {
      if (!otp.trim()) newErrors.otp = t("assets.validation.otpRequired");
      else if (otp.length !== 6) newErrors.otp = t("assets.validation.otpLength");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !asset || !selectedEmployee) return;

    try {
      await onIssue({
        assetId: asset.id,
        issuedToUserId: selectedEmployee.id,
        issueDate,
        expectedReturnDate: expectedReturnDate || undefined,
        purpose,
        condition,
        notes: notes || undefined,
        otp: requireOtp ? otp : "",
      });
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, otp: e?.errors?.[0]?.description || t("assets.invalidOtp") }));
    }
  };

  if (!asset) return null;

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !issuing) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader className="sticky top-0 bg-card z-10">
          <DialogTitle>{t("assets.issueAsset")}</DialogTitle>
        </DialogHeader>

        <form id="issue-form" onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* Asset Info */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.assetInformation")}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("assets.name")}</span>
                  <p className="font-medium">{asset.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.serialNumber")}</span>
                  <p className="font-medium font-mono">{asset.serialNumber || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.category")}</span>
                  <p className="font-medium">{asset.categoryName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.value")}</span>
                  <p className="font-medium">
                    {asset.purchasePrice != null
                      ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2 }).format(asset.purchasePrice)
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Employee Search */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.issueTo")}</h3>
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.employee")} *
                </label>
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(e) => {
                    setEmployeeSearch(e.target.value);
                    if (selectedEmployee) {
                      setSelectedEmployee(null);
                      setOtpGenerated(false);
                      setOtp("");
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                    errors.employee ? "border-destructive" : "border-border"
                  }`}
                  placeholder={t("assets.searchEmployee")}
                  disabled={issuing}
                  data-testid="employee-search-input"
                />
                {searchLoading && (
                  <div className="absolute end-3 top-9 w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                )}
                {errors.employee && <p className="mt-1 text-xs text-destructive">{errors.employee}</p>}

                {showDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {employees.map((emp) => (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => selectEmployee(emp)}
                        className="w-full px-3 py-2 text-start hover:bg-muted text-sm"
                        data-testid={`employee-option-${emp.id}`}
                      >
                        <div className="font-medium text-foreground">{emp.fullName}</div>
                        {emp.department && (
                          <div className="text-xs text-muted-foreground">{emp.department}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedEmployee && (
                <div className="mt-2 bg-primary/10 border border-primary/20 rounded-lg p-2.5 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-medium text-primary">
                    {selectedEmployee.fullName}
                    {selectedEmployee.department && (
                      <span className="text-primary font-normal"> - {selectedEmployee.department}</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Issue Details */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.issueDetails")}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.issueDate")} *
                  </label>
                  <input
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.issueDate ? "border-destructive" : "border-border"
                    }`}
                    disabled={issuing}
                    data-testid="issue-date-input"
                  />
                  {errors.issueDate && <p className="mt-1 text-xs text-destructive">{errors.issueDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.expectedReturnDate")}
                  </label>
                  <input
                    type="date"
                    value={expectedReturnDate}
                    onChange={(e) => setExpectedReturnDate(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    disabled={issuing}
                    data-testid="expected-return-date-input"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.purpose")} *
                </label>
                <textarea
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                    errors.purpose ? "border-destructive" : "border-border"
                  }`}
                  placeholder={t("assets.purposePlaceholder")}
                  disabled={issuing}
                  data-testid="purpose-input"
                />
                {errors.purpose && <p className="mt-1 text-xs text-destructive">{errors.purpose}</p>}
              </div>
            </div>

            {/* Condition */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.conditionAssessment")}</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.currentCondition")}
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value as AssetCondition)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  disabled={issuing}
                  data-testid="condition-select"
                >
                  {[AssetCondition.Excellent, AssetCondition.Good, AssetCondition.Fair, AssetCondition.Poor].map((c) => (
                    <option key={c} value={c}>{conditionLabel(c)}</option>
                  ))}
                </select>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.additionalNotes")}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  placeholder={t("assets.issueNotesPlaceholder")}
                  disabled={issuing}
                  data-testid="issue-notes-input"
                />
              </div>
            </div>

            {/* OTP */}
            {requireOtp && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.acknowledgment")}</h3>
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-primary mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-primary">{t("assets.otpVerificationRequired")}</p>
                      <p className="text-xs text-primary mt-0.5">{t("assets.otpVerificationDesc")}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-end gap-3 mb-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("assets.generateOtp")}
                    </label>
                    <Button
                      type="button"
                      onClick={handleGenerateOtp}
                      disabled={!selectedEmployee || issuing}
                      loading={generatingOtp}
                      data-testid="generate-otp-btn"
                    >
                      {!generatingOtp && (
                        <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      )}
                      {generatingOtp ? t("assets.generating") : t("assets.generateOtp")}
                    </Button>
                  </div>
                </div>

                {!selectedEmployee && (
                  <p className="text-xs text-muted-foreground mb-2">{t("assets.selectEmployeeFirst")}</p>
                )}
                {otpGenerated && (
                  <p className="text-xs text-success font-medium mb-2">{t("assets.otpSent")}</p>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.otp")} *
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(val);
                      if (errors.otp) setErrors((prev) => ({ ...prev, otp: "" }));
                    }}
                    maxLength={6}
                    className={`w-full px-3 py-2 border rounded-lg text-sm font-mono tracking-widest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.otp ? "border-destructive" : "border-border"
                    }`}
                    placeholder="000000"
                    disabled={issuing}
                    data-testid="otp-input"
                  />
                  {errors.otp && <p className="mt-1 text-xs text-destructive">{errors.otp}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{t("assets.otpHint")}</p>
                </div>
              </div>
            )}
          </DialogBody>

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={issuing}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              loading={issuing}
              data-testid="issue-asset-submit-btn"
            >
              {issuing ? t("assets.issuingAsset") : (
                <>
                  <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  {t("assets.issueAsset")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default IssueAssetModal;
